import React, { Fragment, useEffect, useMemo, useState } from 'react'
import Modal from '@ublique/components/LoadingModal'
import NumberInput from '@ublique/components/NumberInput'
import { Select, SelectItem } from '@ublique/components/Select'
import { useTranslation } from 'react-i18next'
import { isEmpty, size } from 'lodash'

import './index.scss'

const NORMAL = 'NORMAL'
const TRIANGULAR = 'TRIANGULAR'
const EXPONENTIAL = 'EXPONENTIAL'
const UNIFORM = 'UNIFORM'
const EMPIRICAL = 'EMPIRICAL'
const MEAN = 'MEAN'
const MODE = 'MODE'
const SQRT = 'SQRT'
const MIN = 'MIN'
const MAX = 'MAX'

const defaultOptions = [
  {
    id: NORMAL,
    text: NORMAL,
    value: NORMAL
  },
  {
    id: TRIANGULAR,
    text: TRIANGULAR,
    value: TRIANGULAR
  },
  {
    id: EXPONENTIAL,
    text: EXPONENTIAL,
    value: EXPONENTIAL
  },
  {
    id: UNIFORM,
    text: UNIFORM,
    value: UNIFORM
  },
  {
    id: EMPIRICAL,
    text: EMPIRICAL,
    value: EMPIRICAL
  }
]

const inputFieldsMap = {
  NORMAL: [
    { id: 1, value: MEAN },
    { id: 2, value: SQRT }
  ],
  EXPONENTIAL: [{ id: 3, value: MEAN }],
  TRIANGULAR: [
    { id: 4, value: MIN },
    { id: 5, value: MODE },
    { id: 6, value: MAX }
  ],
  UNIFORM: [
    { id: 7, value: MIN },
    { id: 8, value: MAX }
  ],
  EMPIRICAL: [
    { id: 9, value: MIN },
    { id: 10, value: MAX }
  ]
}
const getDefaultValues = (type) =>
  type.map(({ value, defaultValue = undefined }) => ({
    [value]: defaultValue ?? 0
  }))

const getPaginationList = (options, t) =>
  options.map(({ text, value }, idx) => (
    <SelectItem key={idx} text={t(`${text}`)} value={value} />
  ))

export default function BuilderExpression({ open, close, data }) {
  const { t } = useTranslation('Models')
  const { t: commonT } = useTranslation('Common')
  const { onSubmit, defaultValue } = data
  const [resultPreview, setResultPreview] = useState()
  const [inputFields, setInputFields] = useState([])
  const [result, setResult] = useState({})

  // Component mount
  useEffect(() => {
    if (isEmpty(defaultValue)) {
      const defaultInputFields = inputFieldsMap[NORMAL]
      setInputFields(defaultInputFields)
      setResult((prev) => ({
        ...prev,
        type: NORMAL,
        values: getDefaultValues(inputFieldsMap[NORMAL])
      }))
    } else {
      // If defaultValue exists

      let distributionType
      Object.keys(inputFieldsMap).forEach((key) => {
        if (defaultValue.search(key) !== -1) {
          distributionType = key
        }
      })

      // If distribution type is not in the map, we set defaults
      if (!distributionType) {
        const defaultInputFields = inputFieldsMap[NORMAL]
        setInputFields(defaultInputFields)
        setResult((prev) => ({
          ...prev,
          type: NORMAL,
          values: getDefaultValues(inputFieldsMap[NORMAL])
        }))
      } else {
        const distributionValues = defaultValue
          .substring(
            defaultValue.indexOf('(') + 1,
            defaultValue.lastIndexOf(')')
          )
          .split(',')

        const inputFieldsWithDefault = inputFieldsMap[distributionType].map(
          (el, idx) => ({
            ...el,
            defaultValue: parseInt(distributionValues[idx])
          })
        )

        setResult((prev) => ({
          ...prev,
          type: distributionType,
          values: getDefaultValues(inputFieldsWithDefault)
        }))
        setInputFields(inputFieldsWithDefault)
      }
    }
  }, [defaultValue, setInputFields, setResult])

  const handleSubmit = () => {
    onSubmit(resultPreview)
    close()
  }

  const onSelect = (type) => {
    setInputFields(inputFieldsMap[type])
    setResult((prev) => ({
      ...prev,
      type,
      values: getDefaultValues(inputFieldsMap[type])
    }))
  }

  const onValueChange = (e, fieldId) => {
    // We need to check if a value already exists for that field and overwrite it if so
    const prevSavedField = []
    if (!isEmpty(result.values)) {
      const keys = []
      result.values.forEach((obj) => {
        Object.keys(obj).forEach((key) => {
          keys.push(key)
        })
      })

      if (keys.includes(fieldId)) {
        prevSavedField.push(fieldId)
      }
    }

    if (size(prevSavedField) > 0) {
      const newValues = result.values.reduce((acc, inc) => {
        if (Object.keys(inc).includes(fieldId)) {
          return [...acc, { [fieldId]: e.imaginaryTarget.valueAsNumber }]
        }
        return [...acc, inc]
      }, [])
      setResult((prev) => ({
        ...prev,
        values: newValues
      }))
    } else {
      // case the value did not exist
      setResult((prev) => ({
        ...prev,
        values: [...prev.values, { [fieldId]: e.imaginaryTarget.valueAsNumber }]
      }))
    }
  }

  // Change preview when change distribution mode
  useEffect(() => {
    if (isEmpty(result.values) || isEmpty(result.type)) return
    const values = []

    inputFieldsMap[result.type].forEach(({ value }) => {
      result.values.forEach((el) => {
        if (Object.keys(el).includes(value)) {
          values.push(el[value])
        }
      })
    })
    setResultPreview(`${result.type}(${values.join(',')})`)
  }, [result])

  const selectOptions = useMemo(() => {
    if (isEmpty(result.type)) return null
    return (
      <Select
        defaultValue={result.type}
        id='id'
        onChange={({ target }) => onSelect(target.value)}
        children={getPaginationList(defaultOptions, t)}
        noLabel
        inline
        light
        size='xl'
      />
    )
  }, [result, onSelect, getPaginationList])

  const submitBtnDisabled = useMemo(() => {
    if (!isEmpty(result.values)) {
      const values = []

      inputFieldsMap[result.type].forEach(({ value }) => {
        result.values.forEach((el) => {
          if (Object.keys(el).includes(value)) {
            values.push(el[value])
          }
        })
      })
      return values.some((val) => isNaN(val))
    }
    return false
  }, [result])

  return (
    <Modal
      open={open}
      size='md'
      className='builder-expression-modal'
      hasScrollingContent={false}
      iconDescription={t('close')}
      modalHeading='Builder expression'
      primaryButtonText={t('Save')}
      confirmLoadingMessage={t('Save')}
      secondaryButtonText={t('Cancel')}
      onRequestSubmit={handleSubmit}
      onRequestClose={close}
      primaryButtonDisabled={submitBtnDisabled}
      preventCloseOnClickOutside
    >
      <h5>{commonT('BuilderExpression.CHOOSE_DISTRIBUTION_TYPE')}</h5>
      <div className='main-content'>
        {selectOptions}
        {/* {!isEmpty(result.expression) && (
          <span className='more-fields-btn' onClick={addInputField}>
            More fields
          </span>
        )} */}
      </div>
      <div className='inputs-wrapper'>
        {!isEmpty(inputFields) &&
          inputFields.map(({ value, id, defaultValue = 0 }, idx) => (
            <>
              <NumberInput
                key={`${id}-${idx}`}
                defaultValue={defaultValue}
                className='input-field'
                label={value}
                onChange={(e) => onValueChange(e, value)}
                invalidText={commonT('Errors.VALUE_NOT_VALID')}
              />

              {/* {idx > 1 && (
              <SubtractAlt16
                onClick={() => removeInputField(id)}
                className='delete-btn'
              />
            )} */}
            </>
          ))}
      </div>

      {!isEmpty(result.type) && (
        <Fragment>
          <div className='modal-footer'>
            {commonT('BuilderExpression.RESULT')} :{' '}
            <span className='result'>{resultPreview}</span>
          </div>
        </Fragment>
      )}
    </Modal>
  )
}

// Potrebbero servire più avanti
//   const addInputField = () => {
//     setInputFields((prev) => [...prev, { id: uniqueId(), value: 0 }])
//   }

//   const removeInputField = (fieldId) => {
//     const newValues = result.values.filter(({ id }) => id === fieldId)
//     const newInputFields = inputFields.filter(({ id }) => id !== fieldId)
//     setInputFields(newInputFields)
//     setResult((prev) => ({
//       ...prev,
//       values: newValues
//     }))
//   }
